import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Box,
  TextField,
  Stack,
  Typography,
  Switch,
  IconButton,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import CachedIcon from '@mui/icons-material/Cached';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CreateIcon from '@mui/icons-material/Create';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Announce from '@/types/Announce.type';
import AnnounceService from '@/services/Announce.service';
import QuickChangeStatus, { QuickChangeStatusForm } from '@/components/actPlan/QuickChangeStatus';
import Announcement from '@/components/actPlan/Announcement';
import Loading from '@/components/common/Loading';
import { Messages } from '@/common/constants';
import { useSnackbar } from 'notistack';
import ActScheduleService from '@/services/ActSchedule.service';
import ActSchedule, { QuickActScheduleRequest } from '@/types/ActSchedule.type';
import ActPlanTable from '@/components/actPlan/ActPlanTable';
import LoadingButton from '@mui/lab/LoadingButton';
import StatusService from '@/services/Status.service';
import Status from '@/types/Status.type';
import { FormProvider, useForm } from 'react-hook-form';
import ActPlanCard from '@/components/actPlan/ActPlanCard';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '@/common/hooks/useAuth';
import HistoryService from '@/services/History.service';
import { HistoryLatestPlaces } from '@/types/History.type';
import URL from '@/common/routes/Url';
import EmployeeService from '@/services/Employee.service';
import CompanyService from '@/services/Company.service';
import SliderAdvertisement from '@/components/common/SliderAdvertisement';

type QuickStatus = Status & { place?: string; status: Status };

const ActPlan: React.FC = () => {
  const useFormMethods = useForm<QuickChangeStatusForm>({ shouldUnregister: false });
  const { reset } = useFormMethods;
  const [loading, setLoading] = useState(true);
  const [quickLoading, setQuickLoading] = useState(false);
  const [departmentOrMyGroupName, setDepartmentOrMyGroupName] = useState<string>('');
  const [actSchedule, setActSchedule] = useState<Array<ActSchedule>>([]);
  const [filteredActSchedule, setFilteredActSchedule] = useState<Array<ActSchedule>>([]);
  const [filterText, setFilterText] = useState<string>('');
  const [announces, setAnnounces] = useState<Array<Announce>>([]);
  const [historyLatestPlaces, setHistoryLatestPlaces] = useState<Array<HistoryLatestPlaces>>([]);
  const [quickStatuses, setQuickStatuses] = useState<Array<QuickStatus>>([]);
  const [reloading, setReloading] = useState<boolean>(false);
  const [reloadTime, setReloadTime] = useState<Dayjs>(dayjs());
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = React.useState(user.autoUpdateFlag);
  const autoRelodingHelpText = '自動更新のON、OFFを切り替えることができます。更新間隔は個人設定で変更可能です。';
  const [contPlan, setContPlan] = useState(0);

  useLayoutEffect(() => {
    setLoading(true);
    setReloading(true);
    (async () => {
      const promise = Promise.all([
        AnnounceService.getMySelf()
          .then((response: { data: Array<Announce> }) => {
            setAnnounces(response.data);
          })
          .catch((e) => {
            enqueueSnackbar(Messages.select.failure);
          }),
        setFilterText(''),
        getQuickList(),
      ]);
      await promise;

      await CompanyService.get().then((response: { data: any }) => {
        setContPlan(response.data.contPlan);
      });

      setLoading(false);
    })();
  }, [searchParams.get('departmentId'), searchParams.get('myGroupId')]);

  useEffect(() => {
    if (!reloading) {
      return;
    }
    (async () => {
      await ActScheduleService.getAll({
        params: {
          departmentId:
            !searchParams.get('departmentId') && !searchParams.get('myGroupId')
              ? user.departments[0].id
              : searchParams.get('departmentId'),
          myGroupId: searchParams.get('myGroupId'),
        },
      })
        .then((response: { data: { actAchedules: Array<ActSchedule>; departmentOrMyGroupName: string } }) => {
          (async () => {
            setActSchedule(response.data.actAchedules);
            setDepartmentOrMyGroupName(response.data.departmentOrMyGroupName);
            setFilteredActSchedule(response.data.actAchedules);
            filtering(response.data.actAchedules);
            setReloadTime(dayjs());
            setReloading(false);
          })();
        })
        .catch((e) => {
          enqueueSnackbar(Messages.select.failure);
        });
      getQuickList();
    })();
  }, [reloading]);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      filtering();
    } else if (event.key === 'Escape') {
      setFilterText('');
      setFilteredActSchedule(actSchedule);
    }
  };

  const filtering = (data?: Array<ActSchedule>) => {
    const tmp = data ?? actSchedule;
    if (!filterText) {
      setFilteredActSchedule(tmp);
      return;
    }
    const Filtered = tmp.filter((row) => {
      return (
        row.style.name === filterText ||
        row.status.displayName === filterText ||
        row.employee.name.indexOf(filterText) !== -1
      );
    });
    setFilteredActSchedule(Filtered);
  };

  const quickUpdate = (values: QuickChangeStatusForm) => {
    (async () => {
      try {
        if (values.returnTimeFlag) {
          navigate(`${URL.plan}/${user.actSchedule.id}`, { state: { quickStatus: values } });
        } else {
          const request: QuickActScheduleRequest = {
            place: values.place,
            statusId: values.id,
          };
          await ActScheduleService.quick(request)
            .then(() => {
              setQuickLoading(false);
              setReloading(true);
              reset();
              enqueueSnackbar(Messages.update.success, { variant: 'info' });
            })
            .catch((error) => {
              enqueueSnackbar(error.response.data.message);
            });
        }
      } catch (error: any) {
        enqueueSnackbar(error.response.data.message);
      }
      return;
    })();
  };

  const getQuickList = () => {
    (async () => {
      await HistoryService.getHistoryLatestPlaces()
        .then((response: { data: Array<HistoryLatestPlaces> }) => {
          setHistoryLatestPlaces(response.data);
        })
        .catch((e) => {
          enqueueSnackbar(Messages.select.failure);
        });
      await StatusService.getQuick()
        .then((response: { data: Array<QuickStatus> }) => {
          setQuickStatuses(response.data);
        })
        .catch((e) => {
          enqueueSnackbar(Messages.select.failure);
        });
      return;
    })();
  };

  useEffect(() => {
    if (checked) {
      const interval = setInterval(
        () => setReloading(true),
        Math.floor(user.autoUpdateTime ? user.autoUpdateTime * 1000 : 300 * 1000),
      );
      return () => clearInterval(interval);
    }
  }, [reloading, checked]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    (async () => {
      try {
        {
          const request = {
            autoUpdateFlag: event.target.checked,
          };
          await EmployeeService.updateAutoFlag(request).catch((error) => {
            enqueueSnackbar(error.response.data.message);
          });
        }
      } catch (error: any) {
        enqueueSnackbar(error.response.data.message);
      }
      return;
    })();
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        {contPlan === 0 ?
          <SliderAdvertisement 
            widthDisplay={{xs: 'none', sm: 'block', md: 'block', lg: 'block'}}
            smallDisplay={{xs: 'block', sm: 'none', md: 'none', lg: 'none'}}
          /> : undefined
        }
      </Grid>
      <Grid item xs={12} sm={12} md={8} lg={8}>
        {contPlan === 1 ?
          <>
            <Box mb={'3px'}>
              <CreateIcon fontSize="small" color="primary" sx={{ verticalAlign: '-4px' }} />
              簡易ステータス更新
            </Box>
            <FormProvider {...useFormMethods}>
              <QuickChangeStatus
                historyLatestPlaces={historyLatestPlaces}
                quickStatuses={quickStatuses}
                loading={quickLoading}
                quickUpdate={quickUpdate}
              />
            </FormProvider>
          </>
          : undefined
        }
        <Stack direction="row" alignItems="center" mt={2}>
          <Grid item xs={12} sm={12} md={8} lg={8}>
            <TextField
              value={filterText}
              label="検索"
              fullWidth
              onKeyDown={handleSearchKeyDown}
              onChange={(e) => setFilterText(e.target.value)}
              style={{ backgroundColor: '#ffffff' }}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        filtering();
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Tooltip
            placement="right"
            title="表示社員内で「社員名」と「ステータス」の検索が可能です。例) 出社.在席.会議..."
          >
            <HelpOutlineIcon color="success" sx={{ marginLeft: 1, fontSize: 20 }} />
          </Tooltip>
        </Stack>
      </Grid>

      <Grid container item rowSpacing={{ xs: 1 }} columnSpacing={{ md: 2 }}>
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack
                direction="row"
                alignItems="center"
                display="inline-flex"
                sx={{
                  minWidth: '6rem',
                }}
              >
                <AccountBoxIcon fontSize="small" color="primary" />
                <Typography
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    overflow: 'hidden',
                  }}
                >{`${departmentOrMyGroupName}`}</Typography>
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {'の一覧'}
                </Typography>
                <Tooltip
                  placement="right"
                  title="リアルタイムの情報ではない場合があるため、都度更新ボタンを押すようにして下さい。"
                >
                  <HelpOutlineIcon color="success" sx={{ marginLeft: 1, fontSize: 20, marginRight: 2 }} />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" display="inline-flex">
                <Typography
                  variant="caption"
                  color="#666666"
                  pr={1}
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  最終更新:{reloadTime.format('MM/DD HH:mm')}
                </Typography>
                <FormGroup sx={{ marginLeft: 2, marginRight: 2 }}>
                  <Tooltip placement="right" title={autoRelodingHelpText}>
                    <FormControlLabel
                      control={<Switch size="small" checked={checked} onChange={handleChange} />}
                      label={
                        <Typography variant="caption" color="primary" noWrap={true} sx={{ marginRight: 0 }}>
                          自動更新
                        </Typography>
                      }
                      sx={{ marginRight: 0 }}
                    />
                  </Tooltip>
                </FormGroup>
                <LoadingButton
                  color="primary"
                  onClick={() => {
                    setReloading(true);
                  }}
                  sx={{ minWidth: '5px', padding: 0 }}
                >
                  <CachedIcon fontSize="small" />
                </LoadingButton>
              </Stack>
            </Box>
            <ActPlanTable actSchedule={filteredActSchedule} />
          </Box>
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                minWidth: '6rem',
              }}
            >
              <AccountBoxIcon fontSize="small" color="primary" />
              <Typography
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  overflow: 'hidden',
                }}
              >{`${departmentOrMyGroupName}`}</Typography>
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                }}
              >
                {'の一覧'}
              </Typography>
              <Tooltip
                placement="right"
                title="リアルタイムの情報ではない場合があるため、都度更新ボタンを押すようにして下さい。"
              >
                <HelpOutlineIcon color="success" sx={{ marginLeft: 1, fontSize: 20 }} />
              </Tooltip>
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <Stack direction="row" alignItems="center" display="inline-flex">
                <Typography
                  variant="caption"
                  color="#666666"
                  pr={1}
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  最終更新:{reloadTime.format('MM/DD HH:mm')}
                </Typography>
                <FormGroup sx={{ marginLeft: 2, marginRight: 2 }}>
                  <Tooltip placement="right" title={autoRelodingHelpText}>
                    <FormControlLabel
                      control={<Switch size="small" checked={checked} onChange={handleChange} />}
                      label={
                        <Typography variant="caption" color="primary" noWrap={true} sx={{ marginRight: 0 }}>
                          自動更新
                        </Typography>
                      }
                      sx={{ marginRight: 0 }}
                    />
                  </Tooltip>
                </FormGroup>
                <LoadingButton
                  color="primary"
                  onClick={() => {
                    setReloading(true);
                  }}
                  sx={{ minWidth: '5px', padding: 0 }}
                >
                  <CachedIcon fontSize="small" />
                </LoadingButton>
              </Stack>
            </Box>
            <Stack spacing={1}>
              {filteredActSchedule.map((row: ActSchedule, index: any) => (
                <ActPlanCard actSchedule={row} key={index} />
              ))}
            </Stack>
          </Box>
        </Grid>
        {contPlan === 1 ?
          <Grid item xs={12} lg={4}>
            <Box>
              <NotificationsIcon fontSize="small" color="primary" sx={{ verticalAlign: '-4px' }} />
              アナウンスの一覧
            </Box>
            <Announcement announces={announces} />
          </Grid>
          : 
          <Grid item xs={12} lg={4}>
            <Box sx={{display: {xs: 'none', sm: 'none', md: 'none', lg: 'block'}, textAlign: 'center', mb: -2}}>
              <a href='https://si.mitani-corp.co.jp/solution/e2move/' target='_blank'>
                <img src={'/manual/336-280e2-hanbai.png'} width='60%' height='60%' style={{border: '1px solid #dcdcdc'}}/>
              </a>
            </Box>
            <Box sx={{display: {xs: 'none', sm: 'none', md: 'none', lg: 'block'}, textAlign: 'center', mt: 2, mb: -2}}>
              <a href='https://si.mitani-corp.co.jp/solution/e2move/' target='_blank'>
                <img src={'/manual/336-280e2-kouji.png'} width='60%' height='60%' style={{border: '1px solid #dcdcdc'}}/>
              </a>
            </Box>
          </Grid>
        }
      </Grid>
    </Grid>
  );
};

export default ActPlan;
